/**
 * Tank formu — tankın İKİ HÂLİ var ve form bunu bir seçimle yönetir.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * SAHADA mı DEPODA mı?
 *
 * Şemadaki `ck_site_tank` ikisini birlikte hareket ettiriyor: saha varsa
 * numara var, saha yoksa numara da yok. Ara hâl YOK. Form bunu iki ayrı alan
 * olarak sorsaydı kullanıcı "sahayı seç, numarayı boş bırak" kombinasyonunu
 * kurabilir ve veritabanı kısıtına çarpardı — kendi yaptığı bir yanlış
 * olmadan. Tek bir "yerleşim" seçimi ikisini birlikte açıp kapatıyor.
 *
 * İkinci kural: KAPASİTE LİSTEDEN SEÇİLİR. Eski sistemde serbest sayıydı ve
 * `tank_capacities`'te karşılığı olmayan 20 m³'lük bir tank oluşmuştu (S13);
 * kg hesabı o tank için sessizce çalışmıyordu.
 * ═══════════════════════════════════════════════════════════════════════════
 */
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useParams, useSearch } from '@tanstack/react-router';
import { tankCreateSchema, type TankCreateBody, type TankCreateInput } from '@gasco/shared';

import { Field, Input, Select } from '../design/Field.tsx';
import {
  FormKabugu,
  IzinKapisi,
  KayitKapisi,
  KaydetmeHatasi,
  SecimYerTutucu,
  degisenAlanlar,
  sayiAlani,
  sayiAlaniBosalabilir,
  useKaydet,
} from './form-parts.tsx';
import { useSahalar, useTank, useTankKapasiteleri, type TankSatiri } from './api.ts';

/** Yerleşim ikilisi — biri değişirse ikisi de gönderilir (`ck_site_tank`). */
const BIRLIKTE = [['siteId', 'tankNumber']] as const;

export function TankYeni(): React.JSX.Element {
  const { siteId } = useSearch({ from: '/korumali/tanklar/yeni' });
  return (
    <IzinKapisi gerekli="add_tank">
      <TankFormu {...(siteId === undefined ? {} : { onSecilenSaha: siteId })} />
    </IzinKapisi>
  );
}

export function TankDuzenle(): React.JSX.Element {
  const { tankId } = useParams({ from: '/korumali/tanklar/$tankId/duzenle' });
  const sorgu = useTank(Number(tankId));

  return (
    <IzinKapisi gerekli="edit_tank">
      <KayitKapisi sorgu={sorgu} bulunamadiBaslik="Tank bulunamadı" donus="/tanklar">
        {(tank) => <TankFormu mevcut={tank} />}
      </KayitKapisi>
    </IzinKapisi>
  );
}

function baslangic(mevcut?: TankSatiri, onSecilenSaha?: number): TankCreateInput {
  return {
    siteId: mevcut?.site_id ?? onSecilenSaha ?? null,
    tankNumber: mevcut?.tank_number ?? null,
    serialNumber: mevcut?.serial_number ?? '',
    capacityM3: mevcut?.capacity_m3 ?? (undefined as unknown as number),
    brand: mevcut?.brand ?? '',
    productionYear: mevcut?.production_year ?? null,
    orientation: mevcut?.orientation ?? null,
  };
}

function TankFormu({
  mevcut,
  onSecilenSaha,
}: {
  readonly mevcut?: TankSatiri;
  readonly onSecilenSaha?: number;
}): React.JSX.Element {
  const duzenleme = mevcut !== undefined;
  const { kaydet, hata } = useKaydet('/tanklar');
  const sahalar = useSahalar();
  const kapasiteler = useTankKapasiteleri();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting, dirtyFields },
  } = useForm<TankCreateInput, unknown, TankCreateBody>({
    resolver: zodResolver(tankCreateSchema),
    defaultValues: baslangic(mevcut, onSecilenSaha),
  });

  const sahaId = watch('siteId');
  const sahada = sahaId !== null && sahaId !== undefined;

  /**
   * Yerleşimi değiştirir — İKİ ALANI BİRLİKTE.
   *
   * "Depoda"ya geçerken numara da temizlenmezse kısıt patlar ve kullanıcı
   * anlamsız bir hata görür. `shouldDirty`: alan elle değişmiş sayılır ki
   * PATCH gövdesine girsin.
   */
  const yerlesimDegistir = (depoya: boolean): void => {
    if (depoya) {
      setValue('siteId', null, { shouldDirty: true });
      setValue('tankNumber', null, { shouldDirty: true });
    } else {
      setValue('tankNumber', 1, { shouldDirty: true });
      setValue('siteId', sahalar.data?.[0]?.id ?? null, { shouldDirty: true });
    }
  };

  const gonder = handleSubmit(async (degerler) => {
    if (duzenleme) {
      const govde = degisenAlanlar(degerler, dirtyFields, BIRLIKTE);
      if (Object.keys(govde).length === 0) return;
      await kaydet(`/tanks/${String(mevcut.id)}`, 'PATCH', govde);
      return;
    }
    await kaydet('/tanks', 'POST', degerler);
  });

  return (
    <FormKabugu
      baslik={duzenleme ? 'Tankı düzenle' : 'Yeni tank'}
      aciklama={duzenleme ? mevcut.serial_number : 'Sahaya kurulu ya da depoda'}
      donus="/tanklar"
      gonderiliyor={isSubmitting}
      onSubmit={(e) => void gonder(e)}
    >
      <Field label="Tank seri numarası" required error={errors.serialNumber?.message}>
        {({ id, required, describedBy, invalid }) => (
          <Input
            id={id}
            required={required}
            aria-describedby={describedBy}
            invalid={invalid}
            {...register('serialNumber')}
          />
        )}
      </Field>

      {/*
       * Yerleşim tek bir seçim: iki alanı birlikte açıp kapatıyor. Radyo
       * düğmesi değil `<select>` — mobilde yerel seçici daha hızlı ve
       * tanıdık (`Field.tsx`'teki gerekçenin aynısı).
       */}
      <Field label="Yerleşim" required hint="Depodaki tankın saha numarası olmaz">
        {({ id, required, describedBy }) => (
          <Select
            id={id}
            required={required}
            aria-describedby={describedBy}
            value={sahada ? 'sahada' : 'depoda'}
            onChange={(e) => yerlesimDegistir(e.target.value === 'depoda')}
          >
            <option value="sahada">Sahaya kurulu</option>
            <option value="depoda">Depoda</option>
          </Select>
        )}
      </Field>

      {sahada && (
        <>
          <Field label="Saha" required error={errors.siteId?.message}>
            {({ id, required, describedBy, invalid }) =>
              sahalar.data === undefined ? (
                <SecimYerTutucu id={id} describedBy={describedBy} />
              ) : (
                <Select
                  id={id}
                  required={required}
                  aria-describedby={describedBy}
                  invalid={invalid}
                  {...register('siteId', sayiAlaniBosalabilir)}
                >
                  <option value="">Saha seçin</option>
                  {sahalar.data.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} ({s.addr_code})
                    </option>
                  ))}
                </Select>
              )
            }
          </Field>

          <Field
            label="Saha içi tank numarası"
            required
            hint="1–12 arası; sahada benzersiz"
            error={errors.tankNumber?.message}
          >
            {({ id, required, describedBy, invalid }) => (
              <Input
                id={id}
                required={required}
                aria-describedby={describedBy}
                invalid={invalid}
                type="number"
                inputMode="numeric"
                min={1}
                max={12}
                {...register('tankNumber', sayiAlaniBosalabilir)}
              />
            )}
          </Field>
        </>
      )}

      <Field
        label="Kapasite"
        required
        hint="Listeden seçilir — kg karşılığı buradan hesaplanır"
        error={errors.capacityM3?.message}
      >
        {({ id, required, describedBy, invalid }) =>
          kapasiteler.data === undefined ? (
            <SecimYerTutucu id={id} describedBy={describedBy} />
          ) : (
            <Select
              id={id}
              required={required}
              aria-describedby={describedBy}
              invalid={invalid}
              {...register('capacityM3', sayiAlani)}
            >
              <option value="">Kapasite seçin</option>
              {kapasiteler.data.map((k) => (
                <option key={k.capacity_m3} value={k.capacity_m3}>
                  {k.capacity_m3} m³ · {k.nominal_kg} kg
                </option>
              ))}
            </Select>
          )
        }
      </Field>

      <Field label="Marka" error={errors.brand?.message}>
        {({ id, describedBy, invalid }) => (
          <Input id={id} aria-describedby={describedBy} invalid={invalid} {...register('brand')} />
        )}
      </Field>

      <Field label="Üretim yılı" error={errors.productionYear?.message}>
        {({ id, describedBy, invalid }) => (
          <Input
            id={id}
            aria-describedby={describedBy}
            invalid={invalid}
            type="number"
            inputMode="numeric"
            {...register('productionYear', sayiAlaniBosalabilir)}
          />
        )}
      </Field>

      <Field label="Konumlandırma" error={errors.orientation?.message}>
        {({ id, describedBy, invalid }) => (
          <Select
            id={id}
            aria-describedby={describedBy}
            invalid={invalid}
            {...register('orientation', {
              setValueAs: (v: unknown) => (v === '' || v === null ? null : v),
            })}
          >
            <option value="">Belirtilmedi</option>
            <option value="horizontal">Yatay</option>
            <option value="vertical">Dikey</option>
          </Select>
        )}
      </Field>

      <KaydetmeHatasi hata={hata} />
    </FormKabugu>
  );
}
